// logic to filter the right issues for user problem, i.e.
// Return this item -> can_return/can_exhange
// Issue with delivery -> etc.
export function getIssueOptions(issueType, issues) {
  const returnIssues = issues.filter((d) => d.issue_type === "return");
  const insuranceIssues = issues.filter((d) => d.issue_type === "insurance");

  if (issueType === "return") {
    return returnIssues.filter((issue) => issue.is_enabled);
  } else {
    return insuranceIssues.filter((issue) => issue.is_enabled);
  }
}

// builds requested resolution outcomes and
// maps issue flags to resolution of what user can choose
export function getResolutionOptions(
  options,
  currentReason,
  can_store_credit,
  allow_exchanges,
) {
  const option = options.find((d) => d.issue_name === currentReason) ?? {};
  const keys = [
    "can_refund",
    "can_reship",
    "can_return",
    allow_exchanges ? "can_exchange" : "",
    can_store_credit ? "can_store_credit" : "",
  ];

  return Object.keys(option)
    .filter((key) => keys.includes(key) && option[key]?.length)
    .map((key) => option[key]);
}

export function getExchangeLabel(options, currentReason) {
  const option = options.find((d) => d.issue_name === currentReason) ?? {};
  return option.can_exchange || "";
}

export function isExchangeResolution(resolution, exchangeLabel) {
  if (!resolution || !exchangeLabel) return false;
  return resolution === exchangeLabel;
}

export function defaultResolution(issueOptions, reason, order) {
  const resolutions = getResolutionOptions(
    issueOptions,
    reason,
    order.store_credits_enabled,
    order.allow_exchanges,
  );
  const onlyResolution = resolutions.length === 1 ? resolutions[0] : "";
  return isExchangeResolution(
    onlyResolution,
    getExchangeLabel(issueOptions, reason),
  )
    ? onlyResolution
    : "";
}

export function siblingExchangeVariants(
  catalog,
  productId,
  orderedVariantId,
  paidPrice,
  exchangeOptions,
) {
  const variants = catalog?.[String(productId)] || [];
  return variants.filter((variant) => {
    if (String(variant.variant_id) === String(orderedVariantId ?? "")) {
      return false;
    }
    return (
      !isOutOfStock(variant) &&
      isWithinExchangePriceRange(variant.price, paidPrice, exchangeOptions)
    );
  });
}

export function isWithinExchangePriceRange(price, paidPrice, exchangeOptions) {
  if (price == null || paidPrice == null) return true;
  const variantPrice = Number(price);
  const paid = Number(paidPrice);
  if (!exchangeOptions?.allow_higher_priced && variantPrice > paid) {
    return false;
  }
  return !(
    exchangeOptions?.allow_lower_priced === false && variantPrice < paid
  );
}

export const differentItemExchangeAllowed = (order) =>
  !!order?.exchange_options?.allow_different_product;

export const EXCHANGE_MODES = {
  SAME_ITEM: "same_item",
  DIFFERENT_ITEM: "different_item",
};

export const DEFAULT_VARIANT_TITLE = "Default Title";

export const resolveExchangeMode = (
  selectedMode,
  hasSameItemOptions,
  differentItemAllowed = true,
) => {
  if (!differentItemAllowed) return EXCHANGE_MODES.SAME_ITEM;
  if (!hasSameItemOptions) return EXCHANGE_MODES.DIFFERENT_ITEM;
  return selectedMode || EXCHANGE_MODES.SAME_ITEM;
};

export const exchangeQuoteItems = (items) =>
  (items ?? [])
    .filter((item) => item.exchangeVariantId)
    .map((item) => ({
      line_item_id: String(item.itemId),
      exchange_variant_id: String(item.exchangeVariantId),
      quantity: Number(item.quantity ?? 1),
    }));

const REFUND_MESSAGE_KEYS = {
  refund_original: "review.exchangeQuote.refundOriginal",
  store_credit: "review.exchangeQuote.refundStoreCredit",
  keep: "review.exchangeQuote.noRefund",
};

export const exchangeQuoteMessageKey = (quote) => {
  if (!quote) return null;
  const difference = Number(quote.net_difference);
  if (difference > 0 && Number(quote.return_shipping_fee) > 0) {
    return "review.exchangeQuote.payWithShipping";
  }
  if (difference > 0) return "review.exchangeQuote.payDifference";
  if (difference < 0) {
    return (
      REFUND_MESSAGE_KEYS[quote.difference_refund] ??
      REFUND_MESSAGE_KEYS.refund_original
    );
  }
  return "review.exchangeQuote.even";
};

export const reviewRequestType = (items) =>
  items?.length && items.every((item) => item.issueType === "return")
    ? "return"
    : "claim";

export const exchangeCreditMessage = ({
  allowHigherPriced,
  allowLowerPriced = true,
  maxUpcharge,
}) => {
  if (!allowHigherPriced) {
    return allowLowerPriced
      ? "resolve.form.exchangeCredit"
      : "resolve.form.exchangeCreditSamePrice";
  }
  return maxUpcharge
    ? "resolve.form.exchangeCreditUpchargeCapped"
    : "resolve.form.exchangeCreditUpcharge";
};

export const exchangeVariantTitle = (productTitle, variantTitle) => {
  if (!variantTitle || variantTitle === DEFAULT_VARIANT_TITLE) {
    return productTitle;
  }
  return `${productTitle} / ${variantTitle}`;
};

function isOutOfStock(variant) {
  if (variant.available_for_sale === false) return true;
  return (
    variant.inventory_tracked === true && (variant.inventory_quantity ?? 0) <= 0
  );
}

export function canFileInsuranceClaim(order) {
  return !!order?.shipping_protection_paid;
}

export function canRequestReturn(order) {
  return !!(order?.allow_returns || order?.allow_exchanges);
}

export function returnButtonLabel(order) {
  if (order?.allow_returns && order?.allow_exchanges) {
    return "Return or Exchange item";
  }
  if (order?.allow_exchanges) {
    return "Exchange this item";
  }
  return "Return this item";
}

export function isImageUploadRequired(currentReason, merchantIssues) {
  if (!currentReason || !merchantIssues) {
    return false;
  }

  const issue = merchantIssues.find((d) => d.issue_name === currentReason);

  return issue?.image_required === true;
}
